import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

/**
 * Universal Builder Component
 * A reusable component builder that can work with any component schema
 *
 * Usage:
 * <UiBook::UniversalBuilder @schema={{this.componentSchema}} />
 */
export default class UniversalBuilder extends Component {
  @tracked parameters = {};

  constructor() {
    super(...arguments);
    this.initializeParameters();
  }

  /**
   * Initialize parameters with default values from schema
   */
  initializeParameters() {
    if (!this.args.schema?.parameters) return;

    const initialParams = {};
    Object.entries(this.args.schema.parameters).forEach(([key, param]) => {
      initialParams[key] = param.default;
    });

    this.parameters = initialParams;
  }

  /**
   * Set parameter value
   */
  @action
  setParameter(key, value) {
    this.parameters = {
      ...this.parameters,
      [key]: value
    };
  }

  /**
   * Handle input events for text parameters
   */
  @action
  handleTextInput(key, event) {
    this.setParameter(key, event.target.value);
  }

  /**
   * Get component name for rendering
   */
  get componentName() {
    return this.args.schema?.component || '';
  }

  /**
   * Get all parameter keys for iteration
   */
  get parameterKeys() {
    return Object.keys(this.args.schema?.parameters || {});
  }

  /**
   * Get schema parameters for template access
   */
  get schemaParameters() {
    return this.args.schema?.parameters || {};
  }

  /**
   * List of components supported by the universal builder
   */
  get supportedComponents() {
    return [
      'BasicUi::MwButton',
      'BasicUi::MwCheckbox'
    ];
  }

  /**
   * Check if the current component is supported
   */
  get isComponentSupported() {
    return this.supportedComponents.includes(this.componentName);
  }
}